import * as React from "react";

interface UseAsyncTypeaheadSettings<T, ServerResponse = T[]> {
  url: (query: string) => string;
  onSuccess: (data: ServerResponse) => T[];
  defaultOptions?: T[];
}

interface UseAsyncTypeaheadResults<T> {
  isLoading: boolean;
  onSearch: (query: string) => Promise<T[]>;
  options: T[];
}

export const useAsyncTypeahead = <T, ServerResponse = T[]>({
  url,
  onSuccess,
  defaultOptions = [],
}: UseAsyncTypeaheadSettings<
  T,
  ServerResponse
>): UseAsyncTypeaheadResults<T> => {
  const [loading, setLoading] = React.useState(false);
  const [options, setOptions] = React.useState(() => defaultOptions);

  const urlFnRef = React.useRef(url);
  const onSuccessFnRef = React.useRef(onSuccess);

  const cache = React.useRef<Record<string, T[]>>({});

  React.useEffect(() => {
    urlFnRef.current = url;
    onSuccessFnRef.current = onSuccess;
  });

  const handleSearch = React.useCallback(
    async (query: string): Promise<T[]> => {
      query = query.trim();

      if (cache.current[query]) {
        setOptions(cache.current[query]);
        return cache.current[query];
      }

      setLoading(true);

      return fetch(urlFnRef.current(query), {
        method: "GET",
      })
        .then((res) => {
          if (res.ok) {
            return res.json();
          }
          throw Error("Response not ok");
        })
        .then(onSuccessFnRef.current)
        .then((result) => {
          setOptions(result);
          cache.current[query] = result;
          return result;
        })
        .finally(() => {
          setLoading(false);
        });
    },
    []
  );

  return {
    isLoading: loading,
    onSearch: handleSearch,
    options,
  };
};

// Note - Currently duplicating the useAsyncTypeahead hook for EOI location search,
// as it will have an alternate url to hit to get data and filter on the client.

interface UseEoiLocationAsyncTypeaheadSettings<T> {
  apiUrl: (query: string) => string;
  onSuccess: (data: T[]) => T[];
  defaultOptions?: T[];
}

export const useEoiLocationAsyncTypeahead = <T,>({
  apiUrl,
  onSuccess,
  defaultOptions = [],
}: UseEoiLocationAsyncTypeaheadSettings<T>): UseAsyncTypeaheadResults<T> => {
  const [loading, setLoading] = React.useState(false);
  const [options, setOptions] = React.useState(() => defaultOptions);

  const apiUrlFnRef = React.useRef(apiUrl);
  const onSuccessFnRef = React.useRef(onSuccess);
  const cache = React.useRef<Record<string, T[]>>({});

  React.useEffect(() => {
    apiUrlFnRef.current = apiUrl;
    onSuccessFnRef.current = onSuccess;
  }, [onSuccess, apiUrl]);

  const handleSearch = React.useCallback(
    async (query: string): Promise<T[]> => {
      query = query.trim();

      // If CDN data is available and loaded, filter from it
      if (defaultOptions.length > 0) {
        const filteredOptions = defaultOptions.filter((n) =>
          (n as string).toLowerCase().includes(query.toLowerCase())
        );

        setOptions(filteredOptions);
        return filteredOptions;
      }

      // Otherwise, use API (CDN failed or is still loading)
      if (cache.current[query]) {
        return cache.current[query];
      }

      setLoading(true);

      return fetch(apiUrlFnRef.current(query))
        .then((res) => {
          if (!res.ok) throw new Error("Response not ok");
          return res.json();
        })
        .then(onSuccessFnRef.current)
        .then((result) => {
          const locations = (result as any).data.locations as T[];

          setOptions(locations);
          cache.current[query] = locations;
          return locations;
        })
        .catch((error) => {
          console.error("API fetch failed:", error);
          return [];
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [defaultOptions]
  );

  return {
    isLoading: loading,
    onSearch: handleSearch,
    options,
  };
};
